/**
 * Empty Paragraph Plugin
 *
 * Problem
 * -------
 * Standard Markdown has no concept of an "empty paragraph". Blank lines are
 * just block separators and get collapsed when the document is re-parsed.
 * When a user presses Enter on an already-empty line Lexical creates an empty
 * ParagraphNode. Without special handling that node is silently dropped on the
 * Lexical → markdown → Lexical round-trip, causing unexpected cursor jumps.
 *
 * Solution
 * --------
 * Uses U+00A0 (non-breaking space) as a round-trip marker:
 *
 *   Export:  empty ParagraphNode  →  paragraph containing "\u00A0" text
 *   Import:  paragraph with only "\u00A0"  →  empty Lexical ParagraphNode
 *
 * Both visitors run at priority 1 so they shadow the built-in ones (priority 0)
 * registered by corePlugin. For every other paragraph they call nextVisitor()
 * so normal behaviour is completely unchanged.
 */

import { realmPlugin, addImportVisitor$, addExportVisitor$ } from '@mdxeditor/editor';
import type { LexicalExportVisitor, MdastImportVisitor } from '@mdxeditor/editor';
import { $isParagraphNode, $createParagraphNode, ElementNode, type ParagraphNode } from 'lexical';
import type * as Mdast from 'mdast';

/** Invisible marker used to keep empty paragraphs alive in serialised markdown. */
const EMPTY_PARAGRAPH_MARKER = '\u00A0';

function isMarkerParagraph(node: Mdast.Paragraph): boolean {
  return (
    node.children.length === 1 &&
    node.children[0].type === 'text' &&
    (node.children[0] as Mdast.Text).value === EMPTY_PARAGRAPH_MARKER
  );
}

// ── export: Lexical ParagraphNode → mdast ───────────────────────────────────

const EmptyParagraphExportVisitor: LexicalExportVisitor<ParagraphNode, Mdast.Paragraph> = {
  priority: 1, // beats the built-in LexicalParagraphVisitor (priority 0)
  testLexicalNode: $isParagraphNode,
  visitLexicalNode({ lexicalNode, mdastParent, actions }) {
    if (lexicalNode.getChildrenSize() === 0) {
      // Empty paragraph — emit a paragraph with the marker so it survives
      // serialisation and can be detected on re-import.
      actions.appendToParent(mdastParent, {
        type: 'paragraph',
        children: [{ type: 'text', value: EMPTY_PARAGRAPH_MARKER }],
      } as Mdast.Paragraph);
    } else {
      // Normal paragraph — delegate to the default visitor.
      actions.nextVisitor();
    }
  },
};

// ── import: mdast paragraph → Lexical ParagraphNode ─────────────────────────

const EmptyParagraphImportVisitor: MdastImportVisitor<Mdast.Paragraph> = {
  priority: 1, // beats the built-in MdastParagraphVisitor (priority 0)
  testNode: 'paragraph',
  visitNode({ mdastNode, lexicalParent, actions }) {
    if (isMarkerParagraph(mdastNode)) {
      // Marker paragraph — recreate the empty Lexical paragraph without
      // visiting the U+00A0 child so the marker is never shown to the user.
      (lexicalParent as unknown as ElementNode).append($createParagraphNode());
    } else {
      // Normal paragraph — delegate to the default visitor.
      actions.nextVisitor();
    }
  },
};

// ── plugin ───────────────────────────────────────────────────────────────────

export const emptyParagraphPlugin = realmPlugin({
  init(realm) {
    realm.pubIn({
      [addExportVisitor$]: EmptyParagraphExportVisitor,
      [addImportVisitor$]: EmptyParagraphImportVisitor,
    });
  },
});

